import { useCallback, useEffect, useMemo, useState } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  SxProps,
  TextField,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { SlimUser } from 'api/models/SlimUser';
import {
  COLUMNS_HIDDEN_BY_DEFAULT,
  DEAL_NAME_KEY,
  ENGAGEMENT_MANAGER_KEY,
  FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  STATUS_KEY,
  TOGGLEABLE_COLUMNS,
  VALUATION_DATE_KEY,
} from 'common/constants/analyst-dashboards';
import type { ColType } from 'common/types/summaryDefinitions';
import type { ValueType } from 'common/types/summaryDefinitions/valueTypes/valueType';
import { useZoom } from 'context/ZoomContext';
import { columns } from 'dashboard409a/pages/Workboard/common/colConfig';
import {
  GroupedUserAutocomplete,
  type GroupedUserAutocompleteDisplayItem,
} from 'dashboard409a/pages/Workboard/Filters/GroupedUserAutocomplete';
import type { SelectedUserWithGroup } from 'dashboard409a/pages/Workboard/UserGroups/UserGroups.types';
import type { AttioStatusOption } from 'dashboard409a/services/attio.types';
import EditColumnsPanel from 'pages/Firms/pages/FirmSummary/EditColumnsPanel';
import type { FilterTypeSelection, TrailingMonthsOption, WorkboardView } from './types';

interface NewViewDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateView: (view: WorkboardView) => void;
  currentColumnVisibility: Record<string, boolean>;
  currentGroupByDeals: boolean;
  statusOptions: AttioStatusOption[];
  statusOptionsLoading: boolean;
  staffUsers: SlimUser[];
  defaultName?: string;
  /** When provided, the dialog operates in edit mode (pre-populated, Save button). */
  initialView?: WorkboardView;
}

// Map workboard column ids to ValueType categories for EditColumnsPanel grouping
const DATE_COLUMN_IDS = new Set(['valuation_date', 'created_at', 'supporting_due_date', 'deal_due']);
const FINANCIAL_COLUMN_IDS = new Set(FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT);

const getWorkboardCategory = (id: string): 'financials' | 'dates' | 'process' => {
  if (FINANCIAL_COLUMN_IDS.has(id)) return 'financials';
  if (DATE_COLUMN_IDS.has(id)) return 'dates';
  return 'process';
};

const workboardValueTypes: ValueType[] = columns
  .filter(col => TOGGLEABLE_COLUMNS.includes(col.id))
  .map(col => ({
    id: col.id,
    displayName: col.label,
    displayFormat: { type: 'text' as const },
    kind: 'simple' as const,
    dependsOn: [],
    metadata: {
      category: getWorkboardCategory(col.id),
    },
  }));

const workboardValueTypeMap = new Map(workboardValueTypes.map(vt => [vt.id, vt]));

// Default visible columns = toggleable columns that are NOT hidden by default
const DEFAULT_WORKBOARD_COLUMNS = TOGGLEABLE_COLUMNS.filter(
  id => !COLUMNS_HIDDEN_BY_DEFAULT.includes(id)
) as ColType[];

// Columns that cannot be removed or reordered
const WORKBOARD_REQUIRED_COLUMNS = new Set([
  STATUS_KEY,
  DEAL_NAME_KEY,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  ENGAGEMENT_MANAGER_KEY,
  VALUATION_DATE_KEY,
]);

const DEFAULT_FILTER_TYPE: FilterTypeSelection = { assigned: false, unassigned: false, all: false };

// Compact textField sx for GroupedUserAutocomplete inside the dialog
const autocompleteInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    minHeight: '32px',
    height: '32px',
    flexWrap: 'nowrap',
    py: '0 !important',
    fontSize: '0.8125rem',
  },
  '& .MuiAutocomplete-input': {
    py: '0 !important',
    fontSize: '0.8125rem',
    minWidth: '20px',
  },
  '& .MuiInputLabel-root': { display: 'none' },
  '& .MuiInputLabel-shrink': { display: 'none' },
};

const NewViewDialog = ({
  open,
  onClose,
  onCreateView,
  currentColumnVisibility,
  currentGroupByDeals,
  statusOptions,
  statusOptionsLoading,
  staffUsers,
  defaultName,
  initialView,
}: NewViewDialogProps) => {
  const isEditMode = Boolean(initialView);
  const { zoomLevel } = useZoom();

  // Derive initial selected columns (ordered) from current visibility
  const initialColumns = useMemo<ColType[]>(
    () => (TOGGLEABLE_COLUMNS.filter(id => currentColumnVisibility[id] !== false) as ColType[]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [viewName, setViewName] = useState('');
  const [editorColumns, setEditorColumns] = useState<ColType[]>(initialColumns);
  const [searchStatus, setSearchStatus] = useState('default');
  const [reviewers, setReviewers] = useState<SelectedUserWithGroup[]>([]);
  const [preparers, setPreparers] = useState<SelectedUserWithGroup[]>([]);
  const [reviewerInput, setReviewerInput] = useState('');
  const [preparerInput, setPreparerInput] = useState('');
  const [filterTypeSelection, setFilterTypeSelection] = useState<FilterTypeSelection>(DEFAULT_FILTER_TYPE);
  const [useTrailingDates, setUseTrailingDates] = useState(true);
  const [trailingMonths, setTrailingMonths] = useState<TrailingMonthsOption>(3);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [groupByDeals, setGroupByDeals] = useState(false);

  useEffect(() => {
    if (open) {
      if (initialView) {
        const f = initialView.filters;
        const visible = TOGGLEABLE_COLUMNS.filter(id => initialView.columnVisibility[id] !== false) as ColType[];
        setViewName(initialView.name);
        setEditorColumns(visible.length > 0 ? visible : DEFAULT_WORKBOARD_COLUMNS);
        setSearchStatus(f.searchStatus);
        setReviewers(f.reviewers ?? []);
        setPreparers(f.preparers ?? []);
        setFilterTypeSelection(f.filterTypeSelection ?? DEFAULT_FILTER_TYPE);
        setUseTrailingDates(f.useTrailingDates);
        setTrailingMonths(f.trailingMonths ?? 3);
        setFromDate(f.fromDate ? new Date(f.fromDate) : null);
        setToDate(f.toDate ? new Date(f.toDate) : null);
        setGroupByDeals(f.groupByDeals);
      } else {
        setViewName(defaultName ?? '');
        setEditorColumns(DEFAULT_WORKBOARD_COLUMNS);
        setSearchStatus('default');
        setReviewers([]);
        setPreparers([]);
        setReviewerInput('');
        setPreparerInput('');
        setFilterTypeSelection(DEFAULT_FILTER_TYPE);
        setUseTrailingDates(true);
        setTrailingMonths(3);
        setFromDate(null);
        setToDate(null);
        setGroupByDeals(currentGroupByDeals);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFilterTypeToggle = (key: keyof FilterTypeSelection) => {
    setFilterTypeSelection(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClearFilters = () => {
    setSearchStatus('default');
    setReviewers([]);
    setPreparers([]);
    setReviewerInput('');
    setPreparerInput('');
    setFilterTypeSelection(DEFAULT_FILTER_TYPE);
    setUseTrailingDates(true);
    setTrailingMonths(3);
    setFromDate(null);
    setToDate(null);
    setGroupByDeals(false);
  };

  // Chip-with-name renderTags for the dialog (image #37 style)
  const renderDialogTags = useCallback(
    (displayItems: GroupedUserAutocompleteDisplayItem[], maxVisible: number) => {
      if (displayItems.length === 0) return null;
      const visible = displayItems.slice(0, maxVisible);
      const remaining = displayItems.length - maxVisible;
      const chipSx: SxProps<Theme> = {
        height: 20,
        fontSize: '0.7rem',
        bgcolor: 'action.hover',
        '& .MuiChip-label': { px: 0.5 },
        '& .MuiChip-icon': { fontSize: '0.75rem', ml: '4px' },
      };
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden', mr: 0.5 }}>
          {visible.map((item, i) => (
            <Chip
              key={i}
              size="small"
              icon={<AccountCircleIcon />}
              label={item.kind === 'group'
                ? item.data.name
                : [item.data.first_name, item.data.last_name].filter(Boolean).join(' ') || item.data.email || ''}
              sx={chipSx}
            />
          ))}
          {remaining > 0 && (
            <Chip
              size="small"
              icon={<AccountCircleIcon />}
              label={`+${remaining}`}
              sx={chipSx}
            />
          )}
        </Box>
      );
    },
    []
  );

  const handleCreate = () => {
    if (!viewName.trim() || editorColumns.length === 0) return;

    const getTrailingRange = () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth() - (trailingMonths - 1), 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 7);
      return { fromDate: from.toISOString(), toDate: to.toISOString() };
    };

    const resolvedDates = useTrailingDates
      ? getTrailingRange()
      : { fromDate: fromDate ? fromDate.toISOString() : null, toDate: toDate ? toDate.toISOString() : null };

    // Build columnVisibility from ordered selection
    const columnVisibility: Record<string, boolean> = {};
    TOGGLEABLE_COLUMNS.forEach(id => {
      columnVisibility[id] = editorColumns.includes(id as ColType);
    });

    onCreateView({
      id: initialView?.id ?? crypto.randomUUID(),
      name: viewName.trim(),
      columnVisibility,
      filters: {
        searchStatus,
        fromDate: resolvedDates.fromDate,
        toDate: resolvedDates.toDate,
        groupByDeals,
        useTrailingDates,
        trailingMonths,
        reviewers,
        preparers,
        filterTypeSelection,
      },
    });
    onClose();
  };

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        {isEditMode ? 'Edit View' : 'Create Workboard View'}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Name your view, choose which columns to display, and drag to reorder them.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Name</Typography>
        <TextField
          fullWidth
          size="small"
          value={viewName}
          onChange={e => setViewName(e.target.value)}
          error={viewName.trim().length === 0 && open}
          helperText={viewName.trim().length === 0 && open ? 'Name is required.' : ' '}
          onKeyDown={e => {
            if (e.key === 'Enter' && viewName.trim()) handleCreate();
          }}
          autoFocus
        />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Columns</Typography>
        <EditColumnsPanel
          availableColumns={workboardValueTypes}
          allValueTypes={workboardValueTypeMap}
          columns={editorColumns}
          onChange={setEditorColumns}
          defaultColumns={DEFAULT_WORKBOARD_COLUMNS}
          requiredColumns={WORKBOARD_REQUIRED_COLUMNS}
        />

        <Divider sx={{ my: 2 }} />

        {/* Filters section */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Filters</Typography>

        {/* Row 1: Model Status | Reviewers | Preparers */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={500}>Model Status</Typography>
            <Select
              size="small"
              value={searchStatus}
              onChange={(e: SelectChangeEvent) => setSearchStatus(e.target.value)}
              sx={{ minWidth: 140, '& .MuiSelect-select': { py: '5px', fontSize: '0.8125rem' } }}
            >
              <MenuItem value="default">Default</MenuItem>
              {statusOptionsLoading ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : (
                statusOptions.map(opt => (
                  <MenuItem key={opt.id.option_id} value={opt.title}>{opt.title}</MenuItem>
                ))
              )}
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={500}>Reviewers</Typography>
            <GroupedUserAutocomplete
              value={reviewers}
              onChange={setReviewers}
              inputValue={reviewerInput}
              onInputChange={setReviewerInput}
              label=""
              placeholder="Select User"
              zoomLevel={zoomLevel}
              maxVisibleTags={2}
              renderTagsOverride={renderDialogTags}
              textFieldSx={autocompleteInputSx}
              autocompleteRootSx={{ minWidth: 160 }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={500}>Preparers</Typography>
            <GroupedUserAutocomplete
              value={preparers}
              onChange={setPreparers}
              inputValue={preparerInput}
              onInputChange={setPreparerInput}
              label=""
              placeholder="Select User"
              zoomLevel={zoomLevel}
              maxVisibleTags={2}
              renderTagsOverride={renderDialogTags}
              textFieldSx={autocompleteInputSx}
              autocompleteRootSx={{ minWidth: 160 }}
            />
          </Stack>
        </Stack>

        {/* Row 2: Assigned / Unassigned / All */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          {(['assigned', 'unassigned', 'all'] as const).map(key => (
            <FormControlLabel
              key={key}
              labelPlacement="start"
              control={
                <Checkbox
                  size="small"
                  checked={filterTypeSelection[key]}
                  onChange={() => handleFilterTypeToggle(key)}
                />
              }
              label={<Typography variant="body2">{key.charAt(0).toUpperCase() + key.slice(1)}</Typography>}
              sx={{ mx: 0, ml: 0 }}
            />
          ))}
        </Stack>

        {/* Row 3: Use Trailing Dates + months or date pickers + Clear Filters */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <FormControlLabel
            labelPlacement="start"
            control={
              <Checkbox
                size="small"
                checked={useTrailingDates}
                onChange={(_, checked) => setUseTrailingDates(checked)}
              />
            }
            label={<Typography variant="body2">Use Trailing Dates</Typography>}
            sx={{ mx: 0, ml: 0 }}
          />
          {useTrailingDates ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={trailingMonths}
              onChange={(_, v: TrailingMonthsOption | null) => {
                if (v) setTrailingMonths(v);
              }}
              sx={{ '& .MuiToggleButton-root': { py: '3px', fontSize: '0.75rem' } }}
            >
              <ToggleButton value={1}>1 Month</ToggleButton>
              <ToggleButton value={2}>2 Month</ToggleButton>
              <ToggleButton value={3}>3 Month</ToggleButton>
            </ToggleButtonGroup>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">From</Typography>
              <DatePicker
                value={fromDate}
                onChange={d => setFromDate(d)}
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'outlined',
                    sx: { width: 140, '& .MuiInputBase-input': { py: '5px', fontSize: '0.8125rem' } },
                  },
                  openPickerButton: { size: 'small', sx: { p: '4px' } },
                }}
              />
              <Typography variant="body2">To</Typography>
              <DatePicker
                value={toDate}
                onChange={d => setToDate(d)}
                minDate={fromDate ?? undefined}
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'outlined',
                    sx: { width: 140, '& .MuiInputBase-input': { py: '5px', fontSize: '0.8125rem' } },
                  },
                  openPickerButton: { size: 'small', sx: { p: '4px' } },
                }}
              />
            </Stack>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            sx={{ textTransform: 'none', fontSize: '0.75rem', py: '3px', px: '10px' }}
          >
            Clear Filters
          </Button>
        </Stack>

        {/* Row 4: Group by Deals */}
        <FormControlLabel
          labelPlacement="start"
          control={
            <Checkbox
              size="small"
              checked={groupByDeals}
              onChange={(_, checked) => setGroupByDeals(checked)}
            />
          }
          label={<Typography variant="body2">Group Company by Deals</Typography>}
          sx={{ mx: 0, ml: 0 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={editorColumns.length === 0 || viewName.trim().length === 0}
        >
          {isEditMode ? 'Save' : 'Create New View'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewViewDialog;
